import React, { useRef, useEffect } from "react";
import { Modal, Box, Typography, List, Divider, IconButton, ButtonBase, Grid } from "@mui/material";
import { GoogleMap, Marker } from "@react-google-maps/api";
import TravelOfferService from "services/TravelOfferService";
import { TravelOffer } from "types/TravelOffer";
import { DriverOffer } from "types/DriverOffer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { TravelCard } from "components/partials/TravelCard/TravelCard";
import { Row } from "components/common/Layout/Row";
import { Cancel, LocalShipping, Refresh, Sync, TimerOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { Ride, RideStatus } from "types/Ride";
import { Column } from "components/common/Layout/Column";
import { useRideSocket } from "hooks/useRideSocket";
import LoadingBox from "components/common/LoadingBox/LoadingBox";

const getRideStatusIcon = (status: RideStatus) => {
  switch (status) {
    case "pickup":
      return <LocalShipping color="primary" />;
    case "in_progress":
      return <Sync color="action" />;
    case "completed":
      return <CheckCircleIcon color="success" />;
    case "cancelled":
      return <Cancel color="error" />;
    default:
      return null;
  }
};

interface TravelOfferDetailsModalProps {
  open: boolean;
  travelOffer: TravelOffer | null;
  ride?: Ride;
  onClose: () => void;
}

const TravelOfferDetailsModal: React.FC<TravelOfferDetailsModalProps> = ({ open, travelOffer, onClose, ride }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const {
    data: driverOffers,
    isLoading,
    isFetching,
    error,
    refetch,
  } = TravelOfferService.useGetTravelOfferDriverOffersQuery(travelOffer ? travelOffer.id : 0, {
    skip: !travelOffer,
    refetchOnMountOrArgChange: true,
  });
  const [cancelRide, cancelResult] = TravelOfferService.useCancelRideMutation();

  const activeRideId = driverOffers?.find((offer: DriverOffer) => offer.ride && offer.status === "accepted")?.ride?.id;
  const driverMarker = useRideSocket(activeRideId);

  const fitToMarkers = (map: google.maps.Map, travelOffer: TravelOffer) => {
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: travelOffer.origin.coordinates[1], lng: travelOffer.origin.coordinates[0] });
    bounds.extend({ lat: travelOffer.destination.coordinates[1], lng: travelOffer.destination.coordinates[0] });
    map.fitBounds(bounds);
  };

  const handleCancelRide = async () => {
    if (ride) {
      await cancelRide(ride.id);
      onClose();
    }
  };

  useEffect(() => {
    if (travelOffer && mapRef.current) {
      fitToMarkers(mapRef.current, travelOffer);
    }
  }, [travelOffer]);

  // useEffect(() => {
  //   if (driverMarker && travelOffer && mapRef.current) {
  //     const bounds = new window.google.maps.LatLngBounds();
  //     bounds.extend({ lat: travelOffer.origin.coordinates[1], lng: travelOffer.origin.coordinates[0] });
  //     bounds.extend({ lat: travelOffer.destination.coordinates[1], lng: travelOffer.destination.coordinates[0] });
  //     bounds.extend({ lat: driverMarker.coordinates[0], lng: driverMarker.coordinates[1] });
  //     mapRef.current.fitBounds(bounds, 50); // 50 px padding
  //   }
  // }, [driverMarker, travelOffer]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", md: "80%" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        {travelOffer && (
          <>
            <Row alignItems="center">
              <Typography variant="h6">Travel Offer Details</Typography>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <Refresh />
              </IconButton>
            </Row>
            {isFetching && <LoadingBox />}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Row>
                  <TravelCard travelOffer={travelOffer} isSelected={false} onClick={() => {}} onEyeClick={() => {}} />
                </Row>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ flex: 1, mr: { md: 2 } }}>
                  <GoogleMap
                    onLoad={(map) => {
                      mapRef.current = map;
                      fitToMarkers(map, travelOffer);
                    }}
                    zoom={15}
                    mapContainerStyle={{ height: "400px", width: "100%" }}
                  >
                    <Marker
                      position={{
                        lat: travelOffer.origin.coordinates[1],
                        lng: travelOffer.origin.coordinates[0],
                      }}
                      label={{
                        text: "A",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    />
                    <Marker
                      position={{
                        lat: travelOffer.destination.coordinates[1],
                        lng: travelOffer.destination.coordinates[0],
                      }}
                      label={{
                        text: "B",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    />
                    {driverMarker && (
                      <Marker
                        position={{
                          lat: driverMarker.coordinates[0],
                          lng: driverMarker.coordinates[1],
                        }}
                        icon={driverMarker.icon}
                      />
                    )}
                  </GoogleMap>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              {isLoading && <Typography>Loading driver offers...</Typography>}
              {error && <Typography color="error">Error loading driver offers.</Typography>}
              {driverOffers && driverOffers.length === 0 && <Typography>No driver offers available.</Typography>}
              {driverOffers && driverOffers.length > 0 && (
                <Column gap={2}>
                  {ride && (
                    <Row>
                      <Column flex={1}>
                        <Row gap={2} alignItems="center">
                          <Typography variant="h6">{ride?.driverOffer.transportDriver?.user?.name}</Typography>
                          <ButtonBase onClick={handleCancelRide}>
                            <Column alignItems="center">
                              <Cancel color="error" />
                              <Typography color="error" variant="body2">
                                cancelar ride
                              </Typography>
                            </Column>
                          </ButtonBase>
                        </Row>
                        <Row gap={0.5}>
                          {getRideStatusIcon(ride.status)}
                          <Typography>{ride.status}</Typography>
                        </Row>
                        <Row gap={0.5}>
                          <TimerOutlined />
                          <Typography>{dayjs(ride.createdAt).format("DD/MM/YYYY hh:mm a")}</Typography>
                        </Row>
                        <Row gap={0.5}>
                          <TimerOutlined />
                          {dayjs(ride?.startTime).format("DD/MM/YYYY hh:mm a")}
                        </Row>
                      </Column>
                    </Row>
                  )}
                  <Typography variant="h6" gutterBottom>
                    Driver Offers
                  </Typography>
                  {driverOffers.map((offer: DriverOffer) => (
                    <Column>
                      <Row key={offer.id} gap={1} flex={1}>
                        <Row flex={1} alignItems="center" gap={1}>
                          {offer.status === "accepted" ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <AccessTimeIcon color="action" />
                          )}
                          <Column>
                            <Typography>{offer.transportDriver?.user?.name}</Typography>
                            <Typography variant="body2">{offer.status}</Typography>
                            <Typography variant="caption">
                              {dayjs(offer.createdAt).format("DD/MM/YYYY hh:mm a")}
                            </Typography>
                          </Column>
                        </Row>
                      </Row>
                      <Divider />
                    </Column>
                  ))}
                </Column>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TravelOfferDetailsModal;
